import {useContext, useEffect, useState} from 'react';
import {AxiosError} from 'axios';
import {useQuery} from '@tanstack/react-query';
import {css} from '@emotion/css';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Modal from 'patient-ping-remedy/packages/modal';
import Input from 'patient-ping-remedy/packages/input';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import {BannerTypes} from 'patient-ping-remedy/packages/banner';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {CampaignConfigResponse} from '@/api/dto/connect';
import {
  FieldWrapper,
  StyledBanner,
} from '@/components/pages/settings/telephony/CampaignConfigForm.styles';

export type CampaignConfigFormResult =
  | { mode: 'create'; rosterId: number; campaignId: string; voicemailConfigId: number | null}
  | { mode: 'edit'; id: number; campaignId: string; voicemailConfigId: number | null};

type Props = {
  isOpen: boolean;
  close: () => void;
  onSave: (result: CampaignConfigFormResult) => void;
  isSaving: boolean;
  saveError: AxiosError | null;
  resetError: () => void;
  existingConfigs: CampaignConfigResponse[];
  editTarget: CampaignConfigResponse | null;
}

export default function CampaignConfigForm({
  isOpen,
  close,
  onSave,
  isSaving,
  saveError,
  resetError,
  existingConfigs,
  editTarget,
}: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const isEdit = !!editTarget;

  const [selectedRoster, setSelectedRoster] = useState<Value | null>(null);
  const [selectedVoicemailConfig, setSelectedVoicemailConfig] = useState<Value | null>(null);
  const [campaignId, setCampaignId] = useState('');

  const { data: rosterOptions = [], isLoading: isLoadingRosters } = useQuery({
    queryKey: ['rosterOptions'],
    queryFn: () => carecoApi?.getRosterOptions(),
    enabled: !!carecoApi && isOpen && !isEdit,
  });

  const { data: voicemailConfigs = [], isLoading: isLoadingVoicemailConfigs} = useQuery({
    queryKey: ['voicemailConfigs'],
    queryFn: () => carecoApi?.getVoicemailConfigs(),
    enabled: !!carecoApi && isOpen
  });

  useEffect(() => {
    if (!isOpen) {
      setSelectedRoster(null);
      setCampaignId('');
      setSelectedVoicemailConfig(null);
      return;
    }
    if (editTarget) {
      setSelectedRoster({
        value: editTarget.rosterId.toString(),
        label: editTarget.rosterName,
      });
      setCampaignId(editTarget.campaignId);
      setSelectedVoicemailConfig({
        value: editTarget.voicemailConfig?.id?.toString() ?? '',
        label: editTarget.voicemailConfig?.name ?? ''
      });
    }
  }, [isOpen, editTarget]);

  // In Add mode, hide rosters that already have an active config — the API would 409.
  // Inactive configs are allowed: POST reactivates them.
  const activeMappedRosterIds = new Set(
    existingConfigs.filter((c) => c.active).map((c) => c.rosterId.toString()),
  );
  const availableRosterOptions: Value[] = rosterOptions.filter(
    (option) => !activeMappedRosterIds.has(option.value as string),
  );

  const is409 = saveError?.response?.status === 409;
  const is404 = saveError?.response?.status === 404;
  const is400 = saveError?.response?.status === 400;

  const trimmedCampaignId = campaignId.trim();

  const originalVoicemailValue = editTarget?.voicemailConfig?.id?.toString() ?? '';
  const currentVoicemailValue = selectedVoicemailConfig?.value ?? null;

  const canSubmit = isEdit
    ? trimmedCampaignId.length > 0 && (trimmedCampaignId !== editTarget!.campaignId
      || currentVoicemailValue !== originalVoicemailValue)
    : !!selectedRoster && trimmedCampaignId.length > 0;

  const handleSave = () => {
    if (!canSubmit) return;
    if (isEdit) {
      onSave({
        mode: 'edit',
        id: editTarget!.id,
        campaignId: trimmedCampaignId,
        voicemailConfigId: currentVoicemailValue === null ? null : Number(currentVoicemailValue)
      });
    } else if (selectedRoster) {
      onSave({
        mode: 'create',
        rosterId: Number(selectedRoster.value),
        campaignId: trimmedCampaignId,
        voicemailConfigId: currentVoicemailValue === null ? null : Number(currentVoicemailValue)
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      hideModal={close}
      headerText={isEdit ? 'Edit Campaign Config' : 'Add Campaign Config'}
      className={css({ width: '70rem' })}
      buttons={[
        <Button
          key="cancel"
          styleType={StyleType.TERTIARY}
          onClick={close}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          onClick={handleSave}
          disabled={!canSubmit || isSaving}
        >
          Save
        </Button>,
      ]}
    >
      {is409 && (
        <StyledBanner type={BannerTypes.WARNING}>
          This group already has an active campaign config. Deactivate the existing config first, then create a new one.
        </StyledBanner>
      )}
      {is404 && (
        <StyledBanner type={BannerTypes.WARNING}>
          We couldn't find that record. It may have been removed by another administrator.
        </StyledBanner>
      )}
      {is400 && (
        <StyledBanner type={BannerTypes.WARNING}>
          The request was rejected by the server. Double-check the campaign ID and try again.
        </StyledBanner>
      )}
      <DropdownList
        id="campaign-config-roster-select"
        label="Group"
        required={true}
        value={isEdit ? { value: editTarget!.rosterId.toString(), label: editTarget!.rosterName } : selectedRoster}
        items={isEdit ? [] : availableRosterOptions}
        handleChange={(value: Value | null) => {
          setSelectedRoster(value);
          resetError();
        }}
        placeholder="Select a group..."
        loading={!isEdit && isLoadingRosters}
        disabled={isEdit}
      />
      <FieldWrapper>
        <Input
          labelText="Campaign ID"
          placeholder="Paste the campaign ID from the AWS Connect console"
          value={campaignId}
          required="required"
          onChange={(e) => {
            setCampaignId(e.target.value);
            resetError();
          }}
        />
        <DropdownList
          id="voicemail-config-select"
          label="Voicemail Config"
          required={true}
          value={selectedVoicemailConfig}
          items={[
            { label: '-- None (no voicemail) --', value: null },
            ...voicemailConfigs.map((config) => ({
              label: config.name,
              value: config.id.toString()
            }))
          ]}
          handleChange={(value: Value | null) => {
            setSelectedVoicemailConfig(value);
            resetError();
          }}
          placeholder="Select a Voicemail Config..."
          loading={isLoadingVoicemailConfigs}
        />
      </FieldWrapper>
    </Modal>
  );
}
